/**
 * Functions for building queries for hit counts.
 * Back end for useHitCounts.ts
 */
import {
  type SP,
  type SearchConfig,
  type SearchField,
  type SearchParams,
  isOptions,
} from './config'
import * as R from 'rambdax'
import {
  colonSplit,
  isEmptyArr,
  logger,
  nullish,
  semiSplit,
  stableJson,
  truthy,
} from 'tizra'
import searchParams from './search-params'

const LOG = logger('search/hit-counts')

export const OPTIMIZATIONS = {
  none: 0,
  safe: 1,
  full: 2,
} as const

interface SPwithPropNames extends Partial<SP> {
  propNames: string[]
}

export function fieldDepthSps({
  config,
  depths,
  field,
  params,
  optimizations: o = 'safe',
}: {
  config: SearchConfig
  depths: SearchConfig['depths']
  field: SearchField
  params: SearchParams
  optimizations?: keyof typeof OPTIMIZATIONS
}): SPwithPropNames[] {
  const logName = `${config.mode}.fieldDepthSps.${field.name}`
  const log = LOG.logger(logName)

  const propName = field.prop
  if (!propName) {
    log.error('assertion failed: field.prop')
    return []
  }

  if (!isOptions(field.options)) {
    // Field init might have failed, or might not be done loading, or simply
    // might not have any values in the DB.
    log.debug?.('no options to count')
    return []
  }

  let sps = depths
    .map(depth =>
      searchParams(
        config,
        {
          ...params,
          // Clear this field in the prop-values search parameters,
          // since we want to know what the counts WOULD BE if any
          // values on this field were set, rather than WHEN values on
          // this field are set.
          [field.name]: field.multi ? [] : '',
          depth,
        },
        {
          allowEmpty: true,
          // field.api.contribute can use this to customize the query for
          // efficiency.
          hittingOn: field.name,
          logName: `${logName}.${depth}`,
        },
      ),
    )
    .filter(truthy)

  // This handles 'none' along with invalid values.
  if (!OPTIMIZATIONS[o]) {
    return sps.map(sp => ({...sp, propNames: [propName]}))
  }

  // Prep for grouping by converting any singular "all" rules to "any".
  sps = sps.map(sp => {
    if (sp.all?.length === 1 && !sp.any?.length) {
      log.debug?.('Converting singular all to any', sp)
      const {all, ...rest} = sp
      return {...rest, any: all}
    }
    return sp
  })

  // Group any depths that can be grouped to achieve the same hit count
  // summation.
  const keyFn = ({any, ...sp}: Partial<SP>) => stableJson(sp)
  const grouped = R.groupBy(keyFn, sps) as Record<string, Partial<SP>[]>
  sps = R.values(grouped).flatMap(group => {
    if (group.length < 2) return group
    if (group.some(sp => sp.any?.some(notStrict))) {
      log.debug?.('Not grouping because missing strict', group)
      return group
    }
    if (metaTypesIntersect(group)) {
      log.debug?.('Not grouping because meta-types intersect', group)
      return group
    }
    log.debug?.('Grouping', group)
    return [
      {
        ...group[0],
        any: group.flatMap(sp => sp.any || []).sort(),
      } as Partial<SP>,
    ]
  })

  if (OPTIMIZATIONS[o] <= OPTIMIZATIONS.safe) {
    return sps.map(sp => ({...sp, propNames: [propName]}))
  }

  if (!smellsDefault(params[field.name], field.defaultValue)) {
    // There's no point augmenting propNames if this field has a value, because
    // we are nulling it in these search terms, and therefore this query won't
    // match any of the other useHitCounts queries in progress.
    log.debug?.('Not augmenting propNames because this field has a value')
    return sps.map(sp => ({...sp, propNames: [propName]}))
  }

  const propNames = Object.values(config.fields)
    .filter(f => {
      if (f.name === field.name) return true
      if (!fieldWantsHits(f)) return false
      if (!f.defaultOpen && Array.isArray(config.defaultOpen)) {
        log
          .once('defaultOpen-array')
          ?.warn(
            'skipping propNames optimization because config.defaultOpen is an array',
          )
        return false
      }
      if (f.defaultOpen === false) return false
      if (!f.defaultOpen && !config.defaultOpen) return false
      return smellsDefault(params[f.name], f.defaultValue)
    })
    .map(f => f.prop!) // can trust because of fieldWantsHits()

  return sps.map(sp => ({...sp, propNames}))
}

export function fieldWantsHits(field: SearchField) {
  return field.hits !== false && !!field.prop
}

function isStrict(s: string) {
  return s.startsWith('strict:') || s.includes(':strict:')
}

function notStrict(s: string) {
  return !isStrict(s)
}

function termMetaTypes(s: string) {
  const mt = colonSplit(s).find(s => s === 'toc-entry' || /^[A-Z]/.test(s))
  return semiSplit(mt)
}

function uniqueMetaTypes(ss: string[]): string[] {
  return Array.from(new Set(ss.flatMap(termMetaTypes)))
}

function metaTypesIntersect(sps: Partial<SP>[]) {
  const gatheredMts = sps.flatMap(sp => uniqueMetaTypes(sp.any || []))
  return gatheredMts.length > new Set(gatheredMts).size
}

function smellsDefault(v: unknown, dv: unknown) {
  if (dv && !isEmptyArr(dv)) return R.equals(v, dv)
  if (nullish(v)) return true
  if (isEmptyArr(v)) return true
  return false
}
